/**
 * Detects a password-recovery link that landed on the wrong route.
 *
 * Supabase reset emails are built from `{{ .RedirectTo }}`. If that redirect
 * URL isn't on the project's allow-list, Supabase silently substitutes the bare
 * `site_url`, so the link arrives at `/` (the login page) instead of
 * `/reset-password` — while the `token_hash`/`type=recovery` params (literal
 * template text) survive in the URL. This lets the app self-heal by forwarding
 * those params to the reset form regardless of the landing path.
 *
 * Returns the query string (leading `?`) to forward to `/reset-password`, or
 * `null` if this isn't a recovery link.
 */
export function getRecoveryRedirectSearch(search: string, hash: string): string | null {
  const query = new URLSearchParams(search.replace(/^\?/, ''));
  const hashParams = new URLSearchParams(hash.replace(/^#/, ''));

  // `type=recovery` is the unambiguous marker (always present in the recovery
  // template) — avoids clashing with the OAuth `?code=` callback.
  const type = query.get('type') ?? hashParams.get('type');
  if (type !== 'recovery') return null;

  const queryString = query.toString();
  if (queryString) return `?${queryString}`;

  // Implicit flow puts everything in the hash — promote it to a query string.
  const hashString = hashParams.toString();
  if (hashString) return `?${hashString}`;

  return null;
}
